import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    X, Plus, Trash2, RadioIcon, MessageSquareText, Loader2, AlertCircle, ArrowUpToLine,
} from 'lucide-react';
import { overlayDismiss } from '../../../lib/overlayDismiss';
// 此 dialog 不直接打 API — 只組 payload 透過 onSaved 回給父層,父層按「儲存」才一次 sync

/**
 * SurveyQuestionEditDialog — 新增 / 編輯題目對話框
 *
 * Props:
 *   open       — boolean
 *   onClose    — () => void
 *   onSaved    — (savedQuestion) => void  父層通常 refetch list
 *   avatarId   — 必填
 *   question   — null 或 undefined ⇒ 新增模式;傳入 SurveyQuestion ⇒ 編輯模式
 *
 * 樣式遵循 frontend-design SKILL:Fraunces 區塊標題、JetBrains Mono 小寫
 * 字距大寫 eyebrow、ink-900 黑底主 CTA、accent 強調 focused 輸入、不對稱
 * 間距、編輯文檔感(非 generic SaaS gray).
 */

const TYPE_OPTIONS = [
    { value: 'choice', icon: RadioIcon, label: 'CHOICE', toneKey: 'type.choice' },
    { value: 'open', icon: MessageSquareText, label: 'OPEN', toneKey: 'type.open' },
];

function defaultState(q) {
    return {
        question: q?.question || '',
        questionType: q?.questionType || 'choice',
        customOptions: (q?.customOptions && q.customOptions.length)
            ? q.customOptions.map((o) => ({ label: o.label || '' }))
            : [{ label: '' }, { label: '' }],
        allowMultiple: !!q?.allowMultiple,
        readOptions: !!q?.readOptions,
        randomizeOptions: !!q?.randomizeOptions,
        demographicField: q?.demographicField || '',
        requireSummary: q?.requireSummary !== false,
        aiSummaryPrompt: q?.aiSummaryPrompt || '',
        isActive: q?.isActive !== false,
        placeTop: false,              // 新增模式專用:存好後置頂(成為 Q1,其他題往後挪)
    };
}

export function SurveyQuestionEditDialog({ open, onClose, onSaved, avatarId, question }) {
    const { t } = useTranslation('wsSurvey');
    const isEdit = !!question?.id;
    const [form, setForm] = useState(defaultState(question));
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (open) {
            setForm(defaultState(question));
            setError(null);
            setSaving(false);
        }
    }, [open, question]);

    if (!open) return null;

    const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));
    const setOption = (idx, label) =>
        setForm((f) => ({
            ...f,
            customOptions: f.customOptions.map((o, i) => (i === idx ? { label } : o)),
        }));
    const addOption = () =>
        setForm((f) => ({ ...f, customOptions: [...f.customOptions, { label: '' }] }));
    const removeOption = (idx) =>
        setForm((f) => ({
            ...f,
            customOptions: f.customOptions.length > 1
                ? f.customOptions.filter((_, i) => i !== idx)
                : f.customOptions,
        }));

    const validate = () => {
        if (!form.question.trim()) return t('edit.validate.emptyQuestion');
        if (form.questionType === 'choice') {
            const cleaned = form.customOptions.map((o) => (o.label || '').trim()).filter(Boolean);
            if (cleaned.length < 2) return t('edit.validate.minOptions');
        }
        return null;
    };

    // 不打 API,純粹組 payload 丟回父層。父層的「儲存」按鈕才真的批次 sync DB。
    const handleSave = () => {
        const err = validate();
        if (err) { setError(err); return; }
        setError(null);

        const payload = {
            question: form.question.trim(),
            questionType: form.questionType,
            isActive: form.isActive,
        };
        if (form.questionType === 'choice') {
            payload.useTemplate = false;
            payload.customOptions = form.customOptions
                .map((o) => ({ label: (o.label || '').trim() }))
                .filter((o) => o.label);
            payload.allowMultiple = form.allowMultiple;
            payload.readOptions = form.readOptions;
            payload.randomizeOptions = form.randomizeOptions;
            if (form.demographicField.trim()) {
                payload.demographicField = form.demographicField.trim();
            }
        } else {
            payload.requireSummary = form.requireSummary;
            if (form.aiSummaryPrompt.trim()) {
                payload.aiSummaryPrompt = form.aiSummaryPrompt.trim();
            }
        }

        onSaved?.(payload, { isEdit, placeTop: !isEdit && form.placeTop });
        onClose();
    };

    return (
        // 比 list 高一層 z-index 確保覆蓋在 SurveyQuestionsModal 上面
        <div
            className="fixed inset-0 z-[60] bg-ink-900/50 backdrop-blur-sm grid place-items-center p-4"
            {...overlayDismiss(onClose)}
        >
            <div
                className="bg-cream-50 rounded-lg shadow-2xl w-full max-w-2xl max-h-[92vh] flex flex-col"
                onClick={(e) => e.stopPropagation()}
            >
                {/* ── Header ── */}
                <header className="px-7 py-5 border-b border-cream-200 flex items-start justify-between">
                    <div>
                        <div className="font-mono text-[10px] tracking-[0.18em] uppercase text-accent-700 mb-1">
                            Survey / {isEdit ? 'Edit Question' : 'New Question'}
                        </div>
                        <h2
                            className="text-2xl text-ink-900 leading-tight"
                            style={{ fontFamily: '"Fraunces", "Noto Serif TC", serif', fontWeight: 500 }}
                        >
                            {isEdit ? t('edit.titleEdit') : t('edit.titleNew')}
                        </h2>
                        {isEdit && question?.id && (
                            <p className="font-mono text-[11px] text-ink-400 mt-1 tabular-nums">
                                id: {question.id} · order: {String(question.order ?? '-').padStart(2, '0')}
                            </p>
                        )}
                    </div>
                    <button
                        onClick={onClose}
                        className="text-ink-400 hover:text-ink-900 transition-colors -mt-1"
                        title={t('edit.closeTitle')}
                    >
                        <X size={18} />
                    </button>
                </header>

                {/* ── Body ── */}
                <div className="flex-1 overflow-y-auto px-7 py-6 space-y-7 bg-cream-100/30">

                    {/* Question type segmented control */}
                    <Section eyebrow="01 · Question type" title={t('edit.section.type')}>
                        <div className="flex gap-2">
                            {TYPE_OPTIONS.map((opt) => {
                                const Icon = opt.icon;
                                const selected = form.questionType === opt.value;
                                return (
                                    <button
                                        key={opt.value}
                                        onClick={() => setField('questionType', opt.value)}
                                        className={[
                                            'flex-1 flex items-center gap-2 px-4 py-2.5 rounded-md border transition-all',
                                            selected
                                                ? 'border-accent-500 bg-accent-50 shadow-[inset_0_0_0_1px_rgba(200,74,44,0.3)]'
                                                : 'border-cream-200 bg-white hover:border-accent-300',
                                        ].join(' ')}
                                    >
                                        <Icon size={14} className={selected ? 'text-accent-700' : 'text-ink-500'} strokeWidth={1.8} />
                                        <span
                                            className={[
                                                'font-mono text-[10px] tracking-[0.16em]',
                                                selected ? 'text-accent-700' : 'text-ink-500',
                                            ].join(' ')}
                                            style={{ fontWeight: 600 }}
                                        >
                                            {opt.label}
                                        </span>
                                        <span className={['text-[13px] ml-1', selected ? 'text-ink-900' : 'text-ink-600'].join(' ')}>
                                            {t(opt.toneKey)}
                                        </span>
                                    </button>
                                );
                            })}
                        </div>
                    </Section>

                    {/* Question text */}
                    <Section eyebrow="02 · Question text" title={t('edit.section.text')}>
                        <textarea
                            value={form.question}
                            onChange={(e) => setField('question', e.target.value)}
                            placeholder={t('edit.textPlaceholder')}
                            rows={3}
                            className="w-full px-3 py-2 rounded-md border border-cream-200 bg-white text-ink-900 text-[14px] leading-relaxed
                                       focus:outline-none focus:border-accent-500 focus:bg-accent-50/30 transition-colors
                                       resize-y"
                            style={{ fontFamily: '"Fraunces", "Noto Serif TC", serif', fontWeight: 450 }}
                        />
                        <p className="font-mono text-[10px] text-ink-400 mt-1 tabular-nums">
                            {t('edit.charCount', { count: form.question.length })}
                        </p>
                    </Section>

                    {/* Choice-specific: options + flags */}
                    {form.questionType === 'choice' && (
                        <>
                            <Section eyebrow="03 · Options" title={t('edit.section.options')}>
                                <ul className="space-y-1.5">
                                    {form.customOptions.map((opt, idx) => (
                                        <li key={idx} className="flex items-center gap-2">
                                            <span className="font-mono text-[10px] text-ink-400 tabular-nums w-5 text-right">
                                                {String(idx + 1).padStart(2, '0')}
                                            </span>
                                            <input
                                                value={opt.label}
                                                onChange={(e) => setOption(idx, e.target.value)}
                                                placeholder={t('edit.optionPlaceholder', { n: idx + 1 })}
                                                maxLength={50}
                                                className="flex-1 px-3 py-1.5 rounded-md border border-cream-200 bg-white text-ink-900 text-[13px]
                                                           focus:outline-none focus:border-accent-500 focus:bg-accent-50/30 transition-colors"
                                            />
                                            <button
                                                onClick={() => removeOption(idx)}
                                                disabled={form.customOptions.length <= 1}
                                                title={form.customOptions.length <= 1 ? t('edit.keepOneOption') : t('edit.removeOption')}
                                                className="p-1.5 rounded-md text-ink-400 hover:text-accent-700 hover:bg-accent-50 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                                            >
                                                <Trash2 size={12} strokeWidth={1.8} />
                                            </button>
                                        </li>
                                    ))}
                                </ul>
                                <button
                                    onClick={addOption}
                                    className="mt-2 flex items-center gap-1.5 px-3 py-1.5 text-[12px] rounded-md border border-dashed border-ink-300 text-ink-600 hover:border-accent-500 hover:text-accent-700 hover:bg-accent-50/40 transition-colors"
                                >
                                    <Plus size={12} strokeWidth={1.8} /> {t('edit.addOption')}
                                </button>
                            </Section>

                            <Section eyebrow="04 · Behavior" title={t('edit.section.behavior')}>
                                <div className="space-y-2">
                                    <FlagToggle
                                        checked={form.allowMultiple}
                                        onChange={(v) => setField('allowMultiple', v)}
                                        label={t('edit.flag.allowMultiple.label')}
                                        sub={t('edit.flag.allowMultiple.sub')}
                                    />
                                    <FlagToggle
                                        checked={form.readOptions}
                                        onChange={(v) => setField('readOptions', v)}
                                        label={t('edit.flag.readOptions.label')}
                                        sub={t('edit.flag.readOptions.sub')}
                                    />
                                    <FlagToggle
                                        checked={form.randomizeOptions}
                                        onChange={(v) => setField('randomizeOptions', v)}
                                        label={t('edit.flag.randomizeOptions.label')}
                                        sub={t('edit.flag.randomizeOptions.sub')}
                                    />
                                </div>
                                <div className="mt-4 pt-4 border-t border-cream-200">
                                    <DemographicFieldPicker
                                        value={form.demographicField}
                                        onChange={(v) => setField('demographicField', v)}
                                    />
                                </div>
                            </Section>
                        </>
                    )}

                    {/* Open-specific: summary settings */}
                    {form.questionType === 'open' && (
                        <Section eyebrow="03 · AI summary" title={t('edit.section.aiSummary')}>
                            <FlagToggle
                                checked={form.requireSummary}
                                onChange={(v) => setField('requireSummary', v)}
                                label={t('edit.flag.requireSummary.label')}
                                sub={t('edit.flag.requireSummary.sub')}
                            />
                            <label className="block mt-4">
                                <span
                                    className="font-mono text-[10px] tracking-[0.16em] uppercase text-ink-500"
                                    style={{ fontWeight: 600 }}
                                >
                                    {t('edit.summaryPromptLabel')}
                                </span>
                                <textarea
                                    value={form.aiSummaryPrompt}
                                    onChange={(e) => setField('aiSummaryPrompt', e.target.value)}
                                    placeholder={t('edit.summaryPromptPlaceholder')}
                                    rows={3}
                                    className="mt-1.5 w-full px-3 py-2 rounded-md border border-cream-200 bg-white text-ink-900 text-[13px] leading-relaxed
                                               focus:outline-none focus:border-accent-500 focus:bg-accent-50/30 transition-colors resize-y"
                                />
                                <p className="text-[11px] text-ink-500 mt-1 leading-snug">
                                    {t('edit.summaryPromptHint')}
                                </p>
                            </label>
                        </Section>
                    )}

                    {/* 置頂(只在新增模式顯示)— 存好後跑 reorder 把這題挪到 order=1 */}
                    {!isEdit && (
                        <Section eyebrow={form.questionType === 'choice' ? '05 · Position' : '04 · Position'} title={t('edit.section.position')}>
                            <FlagToggle
                                checked={form.placeTop}
                                onChange={(v) => setField('placeTop', v)}
                                label={t('edit.flag.placeTop.label')}
                                sub={t('edit.flag.placeTop.sub')}
                            />
                        </Section>
                    )}

                    {/* isActive */}
                    <Section eyebrow={form.questionType === 'choice' ? (isEdit ? '05 · Status' : '06 · Status') : (isEdit ? '04 · Status' : '05 · Status')} title={t('edit.section.status')}>
                        <FlagToggle
                            checked={form.isActive}
                            onChange={(v) => setField('isActive', v)}
                            label={t('edit.flag.isActive.label')}
                            sub={t('edit.flag.isActive.sub')}
                        />
                    </Section>

                    {/* Error banner */}
                    {error && (
                        <div className="flex items-start gap-2 border border-accent-300 bg-accent-50 text-accent-700 text-[13px] rounded-md px-3 py-2.5">
                            <AlertCircle size={14} className="mt-0.5 flex-shrink-0" />
                            <span>{error}</span>
                        </div>
                    )}
                </div>

                {/* ── Footer ── */}
                <footer className="px-7 py-3 border-t border-cream-200 flex items-center justify-between bg-cream-50">
                    <p className="font-mono text-[10px] tracking-[0.15em] uppercase text-ink-400">
                        {isEdit ? 'Editing existing question' : 'Creating new question'}
                    </p>
                    <div className="flex items-center gap-2">
                        <button
                            onClick={onClose}
                            disabled={saving}
                            className="px-4 py-1.5 text-[13px] rounded-md text-ink-700 hover:bg-cream-200 transition-colors"
                        >
                            {t('common:actions.cancel')}
                        </button>
                        <button
                            onClick={handleSave}
                            disabled={saving}
                            className="flex items-center gap-1.5 px-5 py-1.5 text-[13px] rounded-md bg-ink-900 hover:bg-ink-700 text-cream-50 transition-colors disabled:opacity-60 disabled:cursor-wait"
                        >
                            {saving && <Loader2 size={12} className="animate-spin" />}
                            {isEdit ? t('edit.saveChanges') : t('edit.titleNew')}
                        </button>
                    </div>
                </footer>
            </div>
        </div>
    );
}

// 人口統計欄位下拉 — 預設 5 個常用 + 「自訂」fallback 維持手打輸入
const DEMO_PRESETS = [
    { value: '', labelKey: 'demo.none' },
    { value: 'age', labelKey: 'demo.age' },
    { value: 'gender', labelKey: 'demo.gender' },
    { value: 'region', labelKey: 'demo.region' },
    { value: 'education', labelKey: 'demo.education' },
    { value: 'party', labelKey: 'demo.party' },
    { value: 'occupation', labelKey: 'demo.occupation' },
    { value: 'income', labelKey: 'demo.income' },
    { value: 'marital', labelKey: 'demo.marital' },
    { value: 'ethnicity', labelKey: 'demo.ethnicity' },
];

function DemographicFieldPicker({ value, onChange }) {
    const { t } = useTranslation('wsSurvey');
    const presetValues = DEMO_PRESETS.map((p) => p.value);
    const isCustom = value && !presetValues.includes(value);
    const [customMode, setCustomMode] = useState(isCustom);

    return (
        <div>
            <span
                className="block font-mono text-[10px] tracking-[0.16em] uppercase text-ink-500"
                style={{ fontWeight: 600 }}
            >
                {t('demo.fieldLabel')}
            </span>
            <div className="mt-1.5 flex gap-2">
                {!customMode ? (
                    <select
                        value={value || ''}
                        onChange={(e) => {
                            if (e.target.value === '__custom__') {
                                setCustomMode(true);
                                onChange('');
                            } else {
                                onChange(e.target.value);
                            }
                        }}
                        className="flex-1 px-3 py-1.5 rounded-md border border-cream-200 bg-white text-ink-900 text-[13px] focus:outline-none focus:border-accent-500"
                    >
                        {DEMO_PRESETS.map((p) => (
                            <option key={p.value || '__none__'} value={p.value}>{t(p.labelKey)}</option>
                        ))}
                        <option value="__custom__">{t('demo.customOption')}</option>
                    </select>
                ) : (
                    <>
                        <input
                            value={value || ''}
                            onChange={(e) => onChange(e.target.value)}
                            placeholder={t('demo.customPlaceholder')}
                            className="flex-1 px-3 py-1.5 rounded-md border border-cream-200 bg-white text-ink-900 text-[13px] focus:outline-none focus:border-accent-500 font-mono"
                            autoFocus
                        />
                        <button
                            type="button"
                            onClick={() => { setCustomMode(false); onChange(''); }}
                            className="px-3 py-1.5 text-[11px] text-ink-500 hover:text-ink-700 rounded-md hover:bg-cream-100"
                        >
                            {t('demo.backToPreset')}
                        </button>
                    </>
                )}
            </div>
            <p className="text-[11px] text-ink-500 mt-1.5 leading-snug">
                {t('demo.fieldHint')}
            </p>
        </div>
    );
}

// ── Small reused atoms ────────────────────────────────────────────

function Section({ eyebrow, title, children }) {
    return (
        <section>
            <div className="mb-2">
                <div
                    className="font-mono text-[10px] tracking-[0.18em] uppercase text-accent-700 mb-0.5"
                    style={{ fontWeight: 600 }}
                >
                    {eyebrow}
                </div>
                <h3
                    className="text-[15px] text-ink-900"
                    style={{ fontFamily: '"Fraunces", "Noto Serif TC", serif', fontWeight: 500 }}
                >
                    {title}
                </h3>
            </div>
            {children}
        </section>
    );
}

function FlagToggle({ checked, onChange, label, sub }) {
    return (
        <label className="flex items-start gap-3 cursor-pointer group">
            <span className="relative flex-shrink-0 mt-0.5">
                <input
                    type="checkbox"
                    checked={checked}
                    onChange={(e) => onChange(e.target.checked)}
                    className="peer sr-only"
                />
                <span
                    className={[
                        'block w-9 h-5 rounded-full transition-colors',
                        checked ? 'bg-accent-500' : 'bg-ink-300 group-hover:bg-ink-400',
                    ].join(' ')}
                />
                <span
                    className={[
                        'absolute top-0.5 left-0.5 block w-4 h-4 rounded-full bg-white shadow transition-transform ring-1 ring-black/5',
                        checked ? 'translate-x-4' : 'translate-x-0',
                    ].join(' ')}
                />
            </span>
            <span className="flex-1 min-w-0">
                <span className="block text-[13px] text-ink-900">{label}</span>
                {sub && <span className="block text-[11px] text-ink-500 leading-snug mt-0.5">{sub}</span>}
            </span>
        </label>
    );
}
